// TESSERAX — BOUTIQUE COSMÉTIQUE (le puits de la monnaie méta).
//
// La monnaie (meta.currency = Σ floor(score/1000)) s'accumulait SANS aucun usage :
// une demi-boucle d'engagement. On la dépense ici, en COSMÉTIQUES purs — les
// « chambres de résonance » sont les mondes visuels déjà présents (STYLEDEF dans
// main.ts). Ainsi chaque partie « achète » de la progression, et JAMAIS l'équité
// du classement : rien de jouable n'est à vendre (les chambres ne changent que
// l'esthétique). « Auto » reste gratuit et conserve le cycle de mondes par
// profondeur ; on n'ajoute donc qu'une OPTION, sans retirer la variété existante.
//
// Module PUR : ni THREE ni DOM, pas de Math.random.

export type ShopKind = 'chamber';
export interface ShopItem { id: string; kind: ShopKind; world: number; name: string; price: number; }

// ----- VARIÉTÉ COSMÉTIQUE (MOT-SHOP-VARIETY-001) ------------------------------------
// Au-delà des chambres (mondes), on ouvre TROIS axes cosmétiques PURS — aucun avantage
// de jeu, classement équitable — pour donner à la monnaie de nouveaux puits ET une
// COLLECTION à chasser :
//   · 'title' : un titre affiché dans le profil/hub et sur la carte de partage.
//   · 'seal'  : un SCEAU de console (sigle géométrique + teinte d'accent) à côté du nom.
//   · 'card'  : un style de CARTE DE PARTAGE (fond/cadre de l'image de fin).
// OBTENTION MIXTE : `price>0` = acheté en cristaux ◈ ; `merit` = débloqué AU MÉRITE
// (jalon de maîtrise), jamais acheté. `price===0 && !merit` = défaut gratuit (le « rien »).
export type CosmeticKind = 'title' | 'seal' | 'card';
// Métrique de mérite : cumulés (méta) + décomptes de grades de fin (cf. meta.grades).
export type MeritMetric = 'runs' | 'bascules' | 'bestScore' | 'gradeS' | 'gradeA';
export interface Merit { metric: MeritMetric; value: number; }
export interface CosmeticItem {
  id: string;
  kind: CosmeticKind;
  name: string;        // repli FR (i18n cos.<id>)
  price: number;       // 0 = non achetable (défaut gratuit OU mérite)
  merit?: Merit;       // si présent : débloqué au mérite, jamais acheté
  data?: any;          // charge utile cosmétique (teinte de sceau, style de carte…)
}
export interface MeritCtx { runs: number; bascules: number; bestScore: number; gradeS: number; gradeA: number; }

// Catalogues data-driven (croissent par ajout). Le 1ᵉʳ de chaque liste est le DÉFAUT gratuit.
// MOT-CURRENCY-002 — PALIERS D'ENTRÉE : avant, le premier achat possible était la chambre
// Émeraude à 400 ◈ = 400 000 points cumulés (des dizaines de parties pour un débutant) ; la
// monnaie restait « morte » pendant toute la découverte du jeu. On ajoute deux cosmétiques
// d'ENTRÉE (100/150 ◈ ≈ 1 à 3 parties) pour que la boucle « chaque partie achète de la
// progression » paie dès la première session. Purement cosmétique → zéro impact classement.
export const TITLES: CosmeticItem[] = [
  { id: 'tNone',      kind: 'title', name: 'Sans titre',  price: 0 },
  { id: 'tArpenteur', kind: 'title', name: 'Arpenteur',   price: 150 },
  { id: 'tGeometre',  kind: 'title', name: 'Géomètre',    price: 600 },
  { id: 'tArchitecte',kind: 'title', name: 'Architecte',  price: 1500 },
  { id: 'tTectonic',  kind: 'title', name: 'Tectonicien', price: 0, merit: { metric: 'bascules', value: 50 } },
  { id: 'tSouverain', kind: 'title', name: 'Souverain',   price: 0, merit: { metric: 'gradeS', value: 5 } },
];
export const SEALS: CosmeticItem[] = [
  { id: 'sNone',  kind: 'seal', name: 'Aucun sceau', price: 0,    data: null },
  { id: 'sJade',  kind: 'seal', name: 'Sceau jade',  price: 100,  data: { accent: '#7ef0c0', glyph: '◇' } },   // palier d'entrée (MOT-CURRENCY-002)
  { id: 'sCyan',  kind: 'seal', name: 'Sceau cyan',  price: 500,  data: { accent: '#43d9ff', glyph: '◈' } },
  { id: 'sAmbre', kind: 'seal', name: 'Sceau ambre', price: 1000, data: { accent: '#ffd17a', glyph: '✦' } },
  { id: 'sOr',    kind: 'seal', name: 'Sceau d’or',  price: 0,    merit: { metric: 'gradeS', value: 3 }, data: { accent: '#ffd75e', glyph: '✧' } },
];
export const CARDS: CosmeticItem[] = [
  { id: 'cClassic', kind: 'card', name: 'Carte classique', price: 0,   data: { style: 'classic' } },
  { id: 'cVeines',  kind: 'card', name: 'Carte veines',    price: 800, data: { style: 'veines' } },
  { id: 'cApex',    kind: 'card', name: 'Carte Apex',      price: 0,   merit: { metric: 'bestScore', value: 150000 }, data: { style: 'apex' } },
];
export const COSMETICS: CosmeticItem[] = [...TITLES, ...SEALS, ...CARDS];

export function cosmeticById(id: string): CosmeticItem | undefined { return COSMETICS.find(c => c.id === id); }
export function cosmeticsOf(kind: CosmeticKind): CosmeticItem[] { return COSMETICS.filter(c => c.kind === kind); }
// Défaut gratuit d'une famille (le 1ᵉʳ, price 0 sans mérite).
export function defaultCosmetic(kind: CosmeticKind): CosmeticItem { return cosmeticsOf(kind)[0]; }

// Une condition de mérite est-elle atteinte ? PUR.
export function meritMet(m: Merit, ctx: MeritCtx): boolean {
  return (ctx[m.metric] || 0) >= m.value;
}

// Le cosmétique est-il DISPONIBLE (équipable) ? défaut gratuit, OU acheté, OU mérite atteint.
export function cosmeticAvailable(item: CosmeticItem, owned: string[], ctx: MeritCtx): boolean {
  if (item.price === 0 && !item.merit) return true;            // défaut gratuit
  if (item.merit) return meritMet(item.merit, ctx);            // au mérite (jamais acheté)
  return owned.includes(item.id);                             // acheté
}

// Achetable en boutique ? prix>0, non-mérite, non possédé, monnaie suffisante.
export function canBuyCosmetic(currency: number, owned: string[], item: CosmeticItem): boolean {
  return item.price > 0 && !item.merit && !owned.includes(item.id) && currency >= item.price;
}

// Achat cosmétique PUR (immuable) — symétrique de `buy` pour les chambres.
export function buyCosmetic(currency: number, owned: string[], id: string): { currency: number; owned: string[]; ok: boolean } {
  const it = cosmeticById(id);
  if (!it || !canBuyCosmetic(currency, owned, it)) return { currency, owned, ok: false };
  // Filets défensifs (idempotence) : jamais de monnaie négative, jamais de doublon dans owned.
  return { currency: Math.max(0, currency - it.price), owned: owned.includes(id) ? owned : owned.concat(id), ok: true };
}

// Chambres : `world` = index du monde (STYLEDEF) dans main.ts. La première est
// offerte (socle) ; les autres se débloquent à la monnaie. Prix croissants.
// MOT-CURRENCY-001 — CHAMBRE D'ENTRÉE : ch1 (Émeraude) est ramenée à 400 ◈ →
// atteignable en quelques parties (Σ floor(score/1000)), pour qu'un premier achat
// cosmétique récompense vite le joueur au lieu d'un long tunnel (1ᵉʳ achat à 1500).
// Les 6 mondes (STYLEDEF) étant tous déjà mappés, on ne peut pas ajouter une 7ᵉ
// chambre sans nouvel art : le 1ᵉʳ palier DEVIENT donc le palier d'entrée. Les prix
// des chambres supérieures sont INCHANGÉS. Cosmétique pur → zéro impact gameplay/classement.
export const CHAMBERS: ShopItem[] = [
  { id: 'ch0', kind: 'chamber', world: 0, name: 'Azur',      price: 0 },
  { id: 'ch1', kind: 'chamber', world: 1, name: 'Émeraude',  price: 400 },
  { id: 'ch2', kind: 'chamber', world: 2, name: 'Rose',      price: 1500 },
  { id: 'ch3', kind: 'chamber', world: 3, name: 'Améthyste', price: 2500 },
  { id: 'ch4', kind: 'chamber', world: 4, name: 'Ambre',     price: 2500 },
  { id: 'ch5', kind: 'chamber', world: 5, name: 'Liquide',   price: 4000 },
];

export const SHOP_ITEMS: ShopItem[] = [...CHAMBERS];

export function shopItem(id: string): ShopItem | undefined { return SHOP_ITEMS.find(i => i.id === id); }

// Cosmétiques possédés d'office (tout ce qui est gratuit).
export function defaultOwned(): string[] { return SHOP_ITEMS.filter(i => i.price === 0).map(i => i.id); }

export function isOwned(owned: string[], id: string): boolean {
  const it = shopItem(id); if (!it) return false;
  return it.price === 0 || owned.includes(id);
}

export function canBuy(currency: number, owned: string[], id: string): boolean {
  const it = shopItem(id); if (!it) return false;
  return !isOwned(owned, id) && currency >= it.price;
}

// Achat PUR : renvoie le nouvel état (monnaie + possédés) et si l'achat a eu lieu.
// N'altère pas les entrées (immuable) → testable et sans effet de bord.
export function buy(currency: number, owned: string[], id: string): { currency: number; owned: string[]; ok: boolean } {
  if (!canBuy(currency, owned, id)) return { currency, owned, ok: false };
  const it = shopItem(id)!;
  // Filets défensifs (idempotence) : jamais de monnaie négative, jamais de doublon dans owned.
  return { currency: Math.max(0, currency - it.price), owned: owned.includes(id) ? owned : owned.concat(id), ok: true };
}
